import React from 'react';
import { motion } from 'framer-motion';
import AnimatedText from '../components/AnimatedText';
import UnifiedFooter from '../components/UnifiedFooter';

const PrivacyPage: React.FC = () => {

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-24">
        <motion.div
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-8 space-y-8 transition-colors duration-300"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="space-y-4">
            <AnimatedText
              text="Privacy Policy"
              className="text-3xl font-light text-gray-900 dark:text-gray-100 transition-colors duration-300"
              delay={0.2}
              staggerDelay={0.03}
            />
            <motion.p
              className="text-gray-600 dark:text-gray-400 transition-colors duration-300"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </motion.p>
          </div>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Introduction</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              TTStats.ch ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our website (TTStats.ch) and our mobile application (TTStats).
            </p>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Information We Collect</h2>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">Account Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>Email address (for authentication and account management)</li>
                <li>6-digit PIN codes (sent via email for secure sign-in, expire after single use)</li>
                <li>List of favorite players with associated data (name, club, ELO rating, classification, license number)</li>
                <li>Your selected player profile (name, club, ELO rating, classification, license number) for the "My Profile" feature</li>
                <li>Profile avatar selection (choice of 9 animated avatars for personalization)</li>
                <li>User preferences and display settings</li>
                <li>Account creation date and last login timestamp</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">Web Application</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>Usage data through Google Analytics (page views, session duration, device type)</li>
                <li>Browser type and version</li>
                <li>Session information and preferences</li>
              </ul>
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100 transition-colors duration-300">Mobile Application</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li>Account information (synced with web application)</li>
                <li>App settings and preferences</li>
                <li>App usage data and crash reports</li>
                <li>Purchase information for Pro subscriptions (processed by Apple App Store/Google Play Store)</li>
                <li>Device information for cross-platform syncing</li>
              </ul>
            </div>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
              <li>To authenticate your account via secure 6-digit PIN codes sent to your email</li>
              <li>To provide and maintain our service across web and mobile platforms</li>
              <li>To display your "My Profile" card with your selected player data and personalized avatar</li>
              <li>To save and sync your preferences, favorite players, profile settings, and avatar selection across devices</li>
              <li>To provide personalized features such as quick access to your player statistics and rankings</li>
              <li>To manage Pro subscription features and access (future functionality)</li>
              <li>To send you authentication emails with one-time PIN codes and important service notifications</li>
              <li>To analyze usage patterns and improve our service</li>
              <li>To process in-app purchases and subscription payments</li>
              <li>To provide customer support and respond to your inquiries</li>
              <li>To detect and prevent technical issues, abuse, or fraudulent activity</li>
            </ul>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Data Storage and Security</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              We take data security seriously and implement appropriate measures to protect your information:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
              <li><strong>Data Location:</strong> Your data is stored in Switzerland (Supabase Zurich region) and processed within the EU (Hetzner servers in Germany), ensuring compliance with Swiss and EU data protection regulations.</li>
              <li><strong>Authentication:</strong> We use Supabase Auth for secure, passwordless authentication via 6-digit PIN codes sent to your email. PIN codes expire after a single use or after a short time period. No passwords are stored.</li>
              <li><strong>Data Storage:</strong> Your account information, favorites, profile settings, and avatar preferences are stored securely in Supabase's encrypted database infrastructure with automatic backups.</li>
              <li><strong>Email Communication:</strong> Authentication PIN codes and service notifications are sent via Brevo (formerly Sendinblue) with industry-standard security protocols.</li>
              <li><strong>Data Encryption:</strong> All data transmission is encrypted using HTTPS/TLS protocols.</li>
              <li><strong>Access Control:</strong> Your data is only accessible to you when authenticated, and we do not share your personal information with third parties for marketing purposes.</li>
              <li><strong>Data Minimization:</strong> We only collect and store the minimum data necessary to provide our service.</li>
            </ul>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.0 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Third-party Services</h2>
            <div className="space-y-2">
              <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">We use the following third-party services to provide and improve our platform:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <li><strong>Supabase:</strong> Database and authentication infrastructure for storing user accounts, favorites, and session management</li>
                <li><strong>Brevo (Sendinblue):</strong> Email delivery service for sending authentication magic links and service notifications</li>
                <li><strong>Google Analytics:</strong> Website and app usage analytics for understanding user behavior and improving our service</li>
                <li><strong>Apple App Store:</strong> Payment processing for iOS Pro subscriptions and in-app purchases</li>
                <li><strong>Google Play Store:</strong> Payment processing for Android Pro subscriptions and in-app purchases</li>
              </ul>
              <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
                These services may collect additional information as outlined in their respective privacy policies. We carefully select service providers that comply with data protection regulations.
              </p>
            </div>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Table Tennis Data</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              Player statistics and rankings are sourced from the official Swiss Table Tennis database and are publicly available information. We do not modify or share this data beyond displaying it in our application.
            </p>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Your Rights</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              Under applicable data protection laws (including GDPR and Swiss data protection regulations), you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-600 dark:text-gray-300 transition-colors duration-300">
              <li><strong>Access:</strong> View and export your personal data stored in your account</li>
              <li><strong>Rectification:</strong> Correct any inaccurate or incomplete personal information, including changing your profile selection and avatar</li>
              <li><strong>Deletion:</strong> Permanently delete your account and all associated data (favorites, profile settings, avatar preferences, player profile selection, etc.) directly from the user menu. When you delete your account, all personal data including favorites, profile data, and preferences are immediately and permanently removed. Your email address in the authentication system may be retained for a brief period for security and audit purposes before being permanently purged.</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a machine-readable format</li>
              <li><strong>Opt-out:</strong> Disable analytics tracking through your browser settings</li>
              <li><strong>Objection:</strong> Object to certain data processing activities</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing at any time by deleting your account</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-300 mt-4 transition-colors duration-300">
              To exercise these rights or for any questions about your data, please contact us at <a href="mailto:doruk@ttstats.ch" className="text-[#1C7AD6] hover:text-[#1565C0] transition-colors duration-300">doruk@ttstats.ch</a>. Account deletion can be done immediately through the user menu without contacting us.
            </p>
          </motion.section>

          <motion.section 
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3 }}
          >
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-300">Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-300 transition-colors duration-300">
              If you have any questions about this Privacy Policy, please contact us at:{' '}
              <a href="mailto:doruk@ttstats.ch" className="text-[#1C7AD6] hover:text-[#1565C0] transition-colors duration-300">
                doruk@ttstats.ch
              </a>
            </p>
          </motion.section>
        </motion.div>
      </main>

      {/* Footer */}
      <UnifiedFooter variant="personal" />
    </div>
  );
};

export default PrivacyPage;